import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useWikiMutations } from '@/hooks/useWikiMutations';

export interface WikiSectionNode {
  id: string;
  title: string;
  slug: string;
  anchor_id: string;
  order_index: number;
  content_md: string;
  tags: string[];
  children: WikiSectionNode[];
}

interface EditorTreeProps {
  positId: string;
  sections: WikiSectionNode[];
}

const SectionEditor: React.FC<{ positId: string; node?: WikiSectionNode; parentId?: string | null; onDone?: () => void }>
  = ({ positId, node, parentId = null, onDone }) => {
  const [title, setTitle] = useState(node?.title || '');
  const [content, setContent] = useState(node?.content_md || '');
  const [isPublic, setIsPublic] = useState<boolean>(true);
  const [updateLead, setUpdateLead] = useState<boolean>(false);
  const { upsertSection } = useWikiMutations(positId);

  const handleSave = async () => {
    await upsertSection.mutateAsync({
      posit_id: positId,
      section_id: node?.id,
      parent_id: node ? undefined : parentId,
      title,
      content_md: content,
      is_public: isPublic,
      update_lead: updateLead && !node && !parentId // allow updating lead on top-level new section
    });
    onDone && onDone();
  };

  return (
    <div className="space-y-2 border rounded-md p-3 bg-muted/30">
      <Input placeholder="Section title" value={title} onChange={e => setTitle(e.target.value)} />
      <Textarea placeholder="Markdown content" value={content} onChange={e => setContent(e.target.value)} rows={6} />
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><Switch checked={isPublic} onCheckedChange={setIsPublic} /> Public</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={updateLead} onCheckedChange={setUpdateLead} /> Update lead (top-level)</label>
        <Button size="sm" onClick={handleSave} disabled={upsertSection.isPending}>Save</Button>
      </div>
    </div>
  );
};

const TreeNode: React.FC<{ positId: string; node: WikiSectionNode }>
  = ({ positId, node }) => {
  const [editing, setEditing] = useState(false);
  const [addingChild, setAddingChild] = useState(false);
  const { deleteSection } = useWikiMutations(positId);

  return (
    <div className="ml-4 mt-3">
      <div className="flex items-center gap-2">
        <h3 className="font-medium">{node.title}</h3>
        <Button size="sm" variant="secondary" onClick={() => setEditing(v => !v)}>{editing ? 'Close' : 'Edit'}</Button>
        <Button size="sm" variant="secondary" onClick={() => setAddingChild(v => !v)}>{addingChild ? 'Cancel' : 'Add child'}</Button>
        <Button size="sm" variant="destructive" onClick={async () => { await deleteSection.mutateAsync(node.id); }}>Delete</Button>
      </div>
      {editing && <div className="mt-2"><SectionEditor positId={positId} node={node} onDone={() => setEditing(false)} /></div>}
      {addingChild && <div className="mt-2"><SectionEditor positId={positId} parentId={node.id} onDone={() => setAddingChild(false)} /></div>}
      {node.children?.map(child => (
        <TreeNode key={child.id} positId={positId} node={child} />
      ))}
    </div>
  );
};

export const WikiEditorTree: React.FC<EditorTreeProps> = ({ positId, sections }) => {
  const [addingTop, setAddingTop] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Detail Sections</h2>
        <Button size="sm" onClick={() => setAddingTop(v => !v)}>{addingTop ? 'Cancel' : 'Add top-level section'}</Button>
      </div>
      {addingTop && <SectionEditor positId={positId} parentId={null} onDone={() => setAddingTop(false)} />}
      {sections?.map(node => (
        <TreeNode key={node.id} positId={positId} node={node} />
      ))}
    </div>
  );
};

export default WikiEditorTree;


